// Pure helpers extracted from ProductionDashboard.jsx during the P1
// refactoring (Feb 2026). Kept here so both the main dashboard and any
// future sub-components (ClientSection, GroupCard, ContentCard…) can
// import without duplicating logic.

export const TABS = [
  { value: "todo", label: "Da controllare" },
  { value: "edited", label: "Da montare a mano" },
  { value: "published", label: "Pubblicati" },
];

export const KIND_FILTERS = [
  { value: "", label: "Tutti" },
  { value: "photo", label: "Foto" },
  { value: "video,document", label: "Video & File" },
];

// 8-colour palette mirrors backend ALLOWED_COLOR_TAGS in routers/clients.py.
export const COLOR_PALETTE = [
  { hex: "#ef4444", name: "Rosso" },
  { hex: "#f97316", name: "Arancio" },
  { hex: "#eab308", name: "Giallo" },
  { hex: "#22c55e", name: "Verde" },
  { hex: "#06b6d4", name: "Ciano" },
  { hex: "#3b82f6", name: "Blu" },
  { hex: "#a855f7", name: "Viola" },
  { hex: "#ec4899", name: "Rosa" },
];

// Count how many "cards" a list of messages produces in the UI.
// Each grouped subgroup = 1 card; each ungrouped item = 1 card.
export function countCards(items) {
  if (!items?.length) return 0;
  let cards = 0;
  let lastGid = null;
  for (const m of items) {
    const gid = m.group_id || null;
    if (gid && gid === lastGid) continue;
    cards += 1;
    lastGid = gid;
  }
  return cards;
}

// Counts unique video/photo cards in a list of items (respecting group_id).
// Photos = attachment.kind === "photo"; videos = anything else (video,
// document, null) — same routing rule used elsewhere in the file.
export function countByKind(items) {
  if (!items?.length) return { videos: 0, photos: 0 };
  let videos = 0;
  let photos = 0;
  let lastGid = null;
  for (const m of items) {
    const gid = m.group_id || null;
    const isPhoto = m.attachment?.kind === "photo";
    if (gid && gid === lastGid) continue;
    if (isPhoto) photos += 1;
    else videos += 1;
    lastGid = gid;
  }
  return { videos, photos };
}

export function formatGroupCreatedAt(items) {
  if (!items?.length) return "";
  const ts = items[0]?.created_at;
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatActivity(verb, when, who) {
  const parts = [verb];
  if (when) {
    const d = new Date(when);
    parts.push(
      `il ${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}`
    );
  }
  if (who?.name) parts.push(`da ${who.name.split(" ")[0]}`);
  return parts.join(" ");
}

// Returns { label, tone } where tone is one of:
//   - "muted":  never sent anything
//   - "ok":     last upload <= 7 days ago
//   - "warn":   last upload 8-21 days ago
//   - "danger": last upload >= 22 days ago
export function lastContentBadge(iso) {
  if (!iso) return { label: "Mai ricevuto", tone: "muted" };
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return { label: "Mai ricevuto", tone: "muted" };
  const diffMs = Date.now() - then;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return { label: "Ricevuto: oggi", tone: "ok" };
  if (diffDays === 1) return { label: "Ricevuto: ieri", tone: "ok" };
  if (diffDays <= 7) return { label: `Ricevuto: ${diffDays} gg fa`, tone: "ok" };
  if (diffDays <= 21) return { label: `Ricevuto: ${diffDays} gg fa`, tone: "warn" };
  return { label: `Ricevuto: ${diffDays} gg fa`, tone: "danger" };
}

// Companion to lastContentBadge: shows when the agency staff last flagged
// a task "Lavorato" for that client.
export function lastEditedBadge(iso) {
  if (!iso) return { label: "Mai lavorato", tone: "muted" };
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return { label: "Mai lavorato", tone: "muted" };
  const diffMs = Date.now() - then;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return { label: "Lavorato: oggi", tone: "done" };
  if (diffDays === 1) return { label: "Lavorato: ieri", tone: "done" };
  return { label: `Lavorato: ${diffDays} gg fa`, tone: "done" };
}
